import { z } from 'zod';
import { moneyCents } from './common.js';
import { qualificationStatus } from './enums.js';

// =============================================================================
// MCMV federal program constants
// Update these when the federal program rules change. The current value is
// snapshotted into each qualification_submission, so historical data stays
// interpretable across rule changes.
//
// Current bands (rules in force when last reviewed):
//   Faixa 1   até R$ 3.200    | imóveis até R$ 275.000
//   Faixa 2   R$ 3.200–5.000  | imóveis até R$ 275.000
//   Faixa 3   R$ 5.000–9.600  | imóveis até R$ 400.000
//   Faixa 4   R$ 9.600–13.000 | imóveis até R$ 600.000
// =============================================================================

/** Upper income limit for MCMV (top of Faixa 4) in cents. R$ 13.000,00 → 1_300_000. */
export const MCMV_INCOME_MAX_CENTS = 1_300_000;

/**
 * Lower income floor adopted by Angel/Wesley's broker portfolios: their MRV
 * launches require ~R$ 5.000 minimum family income to fit the installment
 * vs. price-band. Leads below this floor don't get the WhatsApp handoff ·
 * the broker has nothing to sell them. Pure commercial filter, not an MCMV
 * federal rule (Faixas 1 and 2 are legitimately below this).
 *
 * R$ 5.000,00 → 500_000 cents.
 */
export const MCMV_INCOME_MIN_CENTS = 500_000;

/** Minimum age to apply for MCMV financing. */
export const MCMV_MIN_AGE = 18;

// =============================================================================
// Qualification result
// =============================================================================

/**
 * Identifies which criterion the applicant failed. Useful for analytics and
 * copy. Each value is prefixed by its vertical (`pv_` for proteção veicular,
 * unprefixed legacy values are MCMV).
 */
export const failedCriterion = z.enum([
  // MCMV-imobiliário (legacy, unprefixed for backwards compat with stored docs)
  'income_above_max',
  'income_below_min',
  'under_min_age',
  'already_owns_property',
  'already_used_mcmv',
  'no_fgts',
  'restricted_credit',
  'no_down_payment',
  'no_income_tax_declaration',
  // Proteção veicular
  'pv_under_min_age',
  'pv_vehicle_too_old',
  'pv_excluded_vehicle_type',
  'pv_outside_coverage_area',
  'ig_income_below_price',
  'ig_long_timeline',
  'ig_just_browsing',
  'ig_low_down_payment',
]);
export type FailedCriterion = z.infer<typeof failedCriterion>;

export const computedQualification = z
  .object({
    status: qualificationStatus,
    failedCriteria: z.array(failedCriterion),
    /** Score 0–100 derived from how many criteria passed. Used for ranking, not for status. */
    score: z.number().int().min(0).max(100),
    /** Snapshot of the income cap applied at submission time (so the record survives rule changes). */
    incomeCapCentsAtSubmission: moneyCents,
  })
  .strict();

export type ComputedQualification = z.infer<typeof computedQualification>;
